<template>
  <v-container>
    <v-card class="pa-5">
      <!-- Title -->
      <v-card-title class="justify-center">
        <span class="text-h5" style="color: #02803f">
          Purchase Order Tracking Report
        </span>
      </v-card-title>

      <!-- Total -->
      <v-row dense>
        <v-col cols="12">
          <v-alert type="info" dense outlined>
            <strong>Total Records:</strong> {{ poLines.length }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row class="px-4" align="center" justify="space-between" dense>
        <!-- Search -->
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="search"
            label="Search PO #, Job #, Item #, Supplier #, Description # or Status"
            dense
            outlined
            clearable
            append-icon="mdi-magnify"
          />
        </v-col>

        <!-- Editable Date Fields -->
        <v-col cols="12" sm="4" class="d-flex">
          <v-text-field
            v-model="startDate"
            label="Start Date"
            type="date"
            dense
            outlined
            class="mr-2"
          />
          <v-text-field
            v-model="endDate"
            label="End Date"
            type="date"
            dense
            outlined
          />
        </v-col>

        <!-- Filter Icon -->
        <v-col cols="12" sm="4" class="d-flex justify-end align-center">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader>Sort By</v-subheader>
              <v-list-item
                v-for="metric in sortableMetrics"
                :key="metric.value"
                @click="toggleSort(metric.value)"
              >
                <v-list-item-title>
                  <span
                    :class="sortBy === metric.value ? 'font-weight-bold' : ''"
                  >
                    {{ metric.text }}
                    <span v-if="sortBy === metric.value">
                      — {{ sortDesc ? "High to Low" : "Low to High" }}
                    </span>
                  </span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>

      <!-- Loader -->
      <v-card-text v-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary" size="48" />
        <div class="mt-2 font-italic">
          Please wait — loading purchase order data.
        </div>
      </v-card-text>

      <!-- Data Table -->
      <v-data-table
        v-else
        :headers="headers"
        :items="filteredResults"
        dense
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="mt-4 striped-rows"
      >
        <!-- PO Number -->
        <template v-slot:item.poNumber="{ item }">
          {{ item.poNumber || "-" }}
        </template>

        <!-- Date (formatting + fallback) -->
        <template v-slot:item.date="{ item }">
          {{ item.date ? item.date.substring(0, 10) : "-" }}
        </template>

        <!-- Job Number -->
        <template v-slot:item.jobNumber="{ item }">
          {{ item.jobNumber || "-" }}
        </template>

        <!-- Supplier -->
        <template v-slot:item.supplier="{ item }">
          {{ item.supplier || "-" }}
        </template>
      <!-- Status -->
      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" outlined>
          {{ statusText(item.status) }}
        </v-chip>
      </template>
        <!-- Quantity -->
        <template v-slot:item.quantity="{ item }">
          {{
            item.quantity !== undefined && item.quantity !== null
              ? item.quantity
              : "-"
          }}
        </template>

        <!-- Item Number -->
        <template v-slot:item.itemNumber="{ item }">
          {{ item.itemNumber || "-" }}
        </template>

        <!-- Description -->
        <template v-slot:item.description="{ item }">
          {{ item.description || "-" }}
        </template>

        <!-- Unit Price -->
        <template v-slot:item.unitPrice="{ item }">
          {{
            item.unitPrice !== undefined && item.unitPrice !== null
              ? formatCurrency(item.unitPrice)
              : "-"
          }}
        </template>

        <!-- Total -->
        <template v-slot:item.total="{ item }">
          {{
            item.total !== undefined && item.total !== null
              ? formatCurrency(item.total)
              : "-"
          }}
        </template>
      </v-data-table>
    </v-card>
    <v-snackbar v-model="snackbar" timeout="4000" color="success" top right>
  {{ snackbarMsg }}
</v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "PurchaseOrderTracking",
  data() {
    return {
      poLines: [],
      loading: true,
      search: "",
      startDate: "2019-01-01",
      endDate: "2030-12-31",
      sortBy: "date",
      sortDesc: true,
      snackbar: false,
      snackbarMsg: "",
      headers: [
        { text: "PO Number", value: "poNumber" },
        { text: "Date", value: "date" },
        { text: "Job Number", value: "jobNumber" },
          { text: "Supplier", value: "supplier" },
          { text: "Status", value: "status" },  
        { text: "Quantity", value: "quantity" },
        { text: "Item Number", value: "itemNumber" },
        { text: "Description", value: "description" },
        { text: "Price", value: "unitPrice", sortable: true },
        { text: "Total", value: "total", sortable: true },
      ],
      sortableMetrics: [
        { text: "PO Number", value: "poNumber" },
        { text: "Date", value: "date" },
        { text: "Job Number", value: "jobNumber" },
        { text: "Item Number", value: "itemNumber" },
        { text: "Unit Price", value: "unitPrice" },
        { text: "Total", value: "total" },
        { text: "Status", value: "status" },           
      ],
    };
  },
  computed: {
    filteredResults() {
      const keyword = this.search.trim().toLowerCase();
      return this.poLines.filter((line) => {
        return (
          line.poNumber?.toLowerCase().includes(keyword) ||
          line.jobNumber?.toLowerCase().includes(keyword) ||
          line.itemNumber?.toLowerCase().includes(keyword) ||
          line.description?.toLowerCase().includes(keyword) ||
          line.supplier?.toLowerCase().includes(keyword) ||
           line.status?.toLowerCase().includes(keyword)
        );
      });
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const res = await axios.get(`/api/jobs/purchase-order-tracking`, {
          params: {
            startDate: this.startDate,
            endDate: this.endDate,
          },
        });
        this.poLines = res.data?.data || [];
        this.snackbarMsg = `Report generated successfully!`;
        this.snackbar = true;
      } catch (err) {
        console.error("❌ Failed to fetch PO data:", err);
        this.snackbarMsg = "❌ Failed to load report. Please try again.";
        this.snackbar = true;
      } finally {
        this.loading = false;
      }
    },
    toggleSort(field) {
      if (this.sortBy === field) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = field;
        this.sortDesc = true;
      }
    },
    formatCurrency(val) {
      return `$${Number(val || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    statusColor(s) {                               // NEW
    if (!s) return "grey";
    const v = String(s).toLowerCase();
    if (v === "open") return "success";
    if (v === "convertedtobill" || v === "converted to bill") return "warning";
    return "grey";
  },
  statusText(s) {                                // NEW
    if (!s) return "-";
    return s === "ConvertedToBill" ? "Converted to Bill" : s;
  },
  },
  watch: {
    startDate: "fetchData",
    endDate: "fetchData",
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
::v-deep .v-data-table-header th {
  font-size: 13px !important;
  font-weight: 600 !important;
  background-color: #02803f !important;
  color: white !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  vertical-align: middle !important;
}

::v-deep .v-data-table td {
  font-size: 13px !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

::v-deep .v-data-table-header th .v-icon {
  vertical-align: middle;
  margin-left: 4px;
  color: white !important;
}
::v-deep .v-data-table-header th {
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
}
::v-deep .v-data-table td,
::v-deep .v-data-table-header th {
  text-align: center !important;
}
.striped-rows >>> tbody tr:nth-child(odd) {
  background-color: #d1f2d1 !important;
}
.striped-rows >>> tbody tr:nth-child(even) {
  background-color: #eafaea !important;
}
.text-h5 {
  font-size: 1.25rem;
  font-weight: 500;
}
</style>
